import { useState } from 'react';
import { X, Download, Wrench, Clock, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ReportContent } from './ReportContent';
import { ToolsDetailDialog, type ToolDetailItem } from '../ToolsDetailDialog';
import type { Report } from '../types';

const PROMPT_COLLAPSE_CHARS = 200;

function formatResponseTime(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function formatTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

export interface ReportPreviewFooter {
  toolCount: number;
  /** Tool details for the popup; when provided, the tool count is clickable */
  toolDetails?: ToolDetailItem[];
  tokens?: number | null;
  responseTimeMs?: number | null;
  dateLabel: string;
}

interface ReportPreviewProps {
  report: Report;
  onClose: () => void;
  onDownload: () => void;
  isGenerating?: boolean;
  prompt?: string;
  footer?: ReportPreviewFooter;
}

export function ReportPreview({
  report,
  onClose,
  onDownload,
  isGenerating,
  prompt,
  footer,
}: ReportPreviewProps) {
  const hasContent = report.content && report.content.trim().length > 0;
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [toolsDialogOpen, setToolsDialogOpen] = useState(false);
  const promptText = prompt?.trim() ?? '';
  const toolDetails = footer?.toolDetails ?? [];
  const canOpenToolsDialog = footer && footer.toolCount > 0 && toolDetails.length > 0;
  const promptIsLong = promptText.length > PROMPT_COLLAPSE_CHARS;
  const promptDisplay =
    promptIsLong && !promptExpanded
      ? promptText.slice(0, PROMPT_COLLAPSE_CHARS) + '...'
      : promptText;

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="text-sm font-medium">{report.title}</h3>
        <div className="flex items-center gap-1">
          {isGenerating && (
            <span className="mr-1 flex items-center gap-1 rounded-full border bg-muted/50 px-2 py-1 text-xs text-muted-foreground">
              <span className="flex gap-0.5">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary [animation-delay:-0.3s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary [animation-delay:-0.15s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary" />
              </span>
              Generating...
            </span>
          )}
          {!isGenerating && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onDownload}>
              <Download className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-6">
          <div className="mx-auto w-full max-w-6xl space-y-4">
            {(promptText || footer) && (
              <div className="rounded-lg border border-border/60 bg-muted/30 px-4 py-3">
                {promptText && (
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1 whitespace-pre-wrap break-words text-sm text-foreground">
                      {promptDisplay}
                    </div>
                    {promptIsLong && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => setPromptExpanded((e) => !e)}
                        aria-label={promptExpanded ? 'Collapse prompt' : 'Expand prompt'}
                      >
                        {promptExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                )}
                {promptText && footer && <div className="my-2 border-t border-border/60" />}
                {footer && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[10px] text-muted-foreground">
                    {isGenerating && footer.toolCount > 0 ? (
                      <button
                        type="button"
                        onClick={() => canOpenToolsDialog && setToolsDialogOpen(true)}
                        disabled={!canOpenToolsDialog}
                        className="flex items-center gap-1 rounded hover:bg-muted/80 hover:text-foreground transition-colors disabled:opacity-70 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                        title={canOpenToolsDialog ? 'View tool details' : 'Tools used'}
                      >
                        <Wrench className="h-3 w-3" />
                        Using tools ({footer.toolCount})
                      </button>
                    ) : footer.toolCount > 0 ? (
                      <button
                        type="button"
                        onClick={() => canOpenToolsDialog && setToolsDialogOpen(true)}
                        disabled={!canOpenToolsDialog}
                        className="flex items-center gap-1 rounded hover:bg-muted/80 hover:text-foreground transition-colors disabled:opacity-70 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                        title={canOpenToolsDialog ? 'View tool details' : 'Tools used'}
                      >
                        <Wrench className="h-3 w-3" />
                        {footer.toolCount} {footer.toolCount === 1 ? 'tool' : 'tools'}
                      </button>
                    ) : null}
                    {canOpenToolsDialog && (
                      <ToolsDetailDialog
                        open={toolsDialogOpen}
                        onOpenChange={setToolsDialogOpen}
                        items={toolDetails}
                      />
                    )}
                    {!isGenerating && footer.tokens != null && footer.tokens > 0 && (
                      <span className="flex items-center gap-1" title="Tokens used">
                        <Zap className="h-3 w-3" />
                        {formatTokens(footer.tokens)} tokens
                      </span>
                    )}
                    {!isGenerating && footer.responseTimeMs != null && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatResponseTime(footer.responseTimeMs)}
                      </span>
                    )}
                    <span title={footer.dateLabel}>{footer.dateLabel}</span>
                  </div>
                )}
              </div>
            )}
            <div className="rounded-lg border bg-white p-8 shadow-sm">
              {hasContent ? (
                <ReportContent content={report.content!} />
              ) : isGenerating ? (
                <div className="flex items-center gap-1 py-4">
                  <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.3s]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.15s]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" />
                </div>
              ) : (
                <>
                  <div className="border-b pb-4">
                    <h1 className="text-xl font-bold text-gray-900">{report.title}</h1>
                    <p className="mt-1 text-sm text-gray-500">
                      Generated on {report.createdAt.toLocaleDateString()}
                    </p>
                  </div>

                  <div className="mt-6 space-y-6">
                    <section>
                      <h2 className="text-sm font-semibold text-gray-900">No Content</h2>
                      <p className="mt-2 text-sm text-gray-600">
                        This report does not have any generated content yet.
                      </p>
                    </section>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </ScrollArea>

      <style>{`
        .report-markdown h1 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: #111827;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 0.5rem;
        }
        .report-markdown h2 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: #1f2937;
        }
        .report-markdown h3 {
          font-size: 1rem;
          font-weight: 600;
          margin-top: 1rem;
          margin-bottom: 0.5rem;
          color: #374151;
        }
        .report-markdown p {
          margin-bottom: 0.75rem;
          color: #4b5563;
          line-height: 1.6;
        }
        .report-markdown ul, .report-markdown ol {
          margin-left: 1.5rem;
          margin-bottom: 0.75rem;
          color: #4b5563;
        }
        .report-markdown li {
          margin-bottom: 0.25rem;
        }
        .report-markdown table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
          font-size: 0.875rem;
        }
        .report-markdown th {
          background-color: #f3f4f6;
          font-weight: 600;
          text-align: left;
          padding: 0.75rem;
          border: 1px solid #e5e7eb;
        }
        .report-markdown td {
          padding: 0.75rem;
          border: 1px solid #e5e7eb;
        }
        .report-markdown tr:nth-child(even) {
          background-color: #f9fafb;
        }
        .report-markdown strong {
          font-weight: 600;
        }
        .report-markdown em {
          font-style: italic;
        }
        .report-markdown code {
          background-color: #f3f4f6;
          padding: 0.125rem 0.25rem;
          border-radius: 0.25rem;
          font-size: 0.875em;
        }
        .report-markdown pre {
          background-color: #1f2937;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1rem 0;
        }
        .report-markdown pre code {
          background-color: transparent;
          padding: 0;
          color: #e5e7eb;
        }
        .report-markdown hr {
          border: none;
          border-top: 1px solid #e5e7eb;
          margin: 1.5rem 0;
        }
        .report-markdown blockquote {
          border-left: 4px solid #2563eb;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #6b7280;
          font-style: italic;
        }
        .report-markdown .katex-display {
          margin: 1rem 0;
          overflow-x: auto;
          overflow-y: hidden;
        }
      `}</style>
    </div>
  );
}
